(function () {
  const STORAGE_KEY = 'bb-geo-popup-preference';
  const RESET_PARAM = 'reset_geo_popup';
  const BOT_UA_PATTERN =
    /bot|crawl|spider|slurp|googlebot|bingbot|yandex|baiduspider|duckduckbot|facebookexternalhit|lighthouse|headlesschrome|pagespeed/i;

  function isLikelyBot() {
    return BOT_UA_PATTERN.test(navigator.userAgent || '');
  }

  // Testing utility: visiting the page with ?reset_geo_popup=1 clears the
  // popup's saved preference AND Shopify's own localization cookie, then
  // reloads without the param so real detection runs fresh. Opt-in only —
  // never runs unless the query param is explicitly present.
  function handleResetParamAndReload() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(RESET_PARAM)) return false;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* storage unavailable */
    }
    document.cookie = 'localization=; Max-Age=0; path=/';
    document.cookie = 'cart_currency=; Max-Age=0; path=/';
    params.delete(RESET_PARAM);
    const query = params.toString();
    const newUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    window.location.replace(newUrl);
    return true;
  }

  function readPreference() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.confirmedAt || !data.expiresAt) return null;
      if (Date.now() > data.expiresAt) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function writePreference(countryCode, languageCode, days) {
    try {
      const now = Date.now();
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          country: countryCode,
          language: languageCode,
          confirmedAt: now,
          expiresAt: now + days * 24 * 60 * 60 * 1000,
        })
      );
    } catch (e) {
      /* storage unavailable — popup will simply show again next visit */
    }
  }

  function englishCountryName(iso, fallback) {
    if (iso === 'NL') return 'The Netherlands';
    return fallback;
  }

  function initPopup(root) {
    if (root.hidden) return; // merchant disabled the popup

    const dataScript = root.querySelector('#GeoPopupCountryData');
    let countryData = { countries: {}, publishedLocales: [] };
    if (dataScript) {
      try {
        countryData = JSON.parse(dataScript.textContent);
      } catch (e) {
        /* fall back to whatever Shopify rendered server-side */
      }
    }

    const modal = root.querySelector('[ref="modal"]');
    const overlay = root.querySelector('.geo-popup__overlay');
    const countrySelect = root.querySelector('[ref="countrySelect"]');
    const languageSelect = root.querySelector('[ref="languageSelect"]');
    const heading = root.querySelector('[ref="heading"]');
    const form = root.querySelector('[ref="form"]');
    if (!modal || !countrySelect || !languageSelect || !form) return;

    const storageDays = Number(root.dataset.storageDays || 365);
    const previewMode = root.dataset.previewMode === 'true';

    function formatCountryLabel(iso, fallbackName) {
      const info = countryData.countries[iso];
      if (!info) return fallbackName;
      const englishName = englishCountryName(iso, info.englishName);
      if (info.nativeName && info.nativeName !== englishName) {
        return info.nativeName + ' / ' + englishName + ' (' + info.currencySymbol + ')';
      }
      return englishName + ' (' + info.currencySymbol + ')';
    }

    function relabelCountryOptions() {
      Array.from(countrySelect.options).forEach(function (opt) {
        const iso = opt.dataset.iso;
        if (!iso) return; // synthetic "International" option — leave as-is
        opt.textContent = formatCountryLabel(iso, opt.textContent.trim());
      });
    }

    function updateHeading() {
      if (!heading) return;
      const opt = countrySelect.options[countrySelect.selectedIndex];
      const iso = opt ? opt.dataset.iso : '';
      const info = countryData.countries[iso];
      const englishName = info ? info.englishName : opt ? opt.textContent.trim() : 'your country';
      const displayName = englishCountryName(iso, englishName);
      const template = heading.dataset.template || "Looks like you're shopping in {country}!";
      heading.textContent = template.replace('{country}', displayName);
    }

    function updateLanguageOptions(preserveSelection) {
      const opt = countrySelect.options[countrySelect.selectedIndex];
      const iso = opt ? opt.dataset.iso : '';
      const info = countryData.countries[iso];
      const previousValue = languageSelect.value;

      const options = [];
      const nativeLanguages = (info && Array.isArray(info.languages)) ? info.languages : [];
      nativeLanguages.forEach(function (lang) {
        if (!lang.published) return; // never offer an unpublished/unavailable language
        if (lang.code === 'en') return; // avoid a duplicate English entry
        options.push({ code: lang.code, label: lang.native });
      });
      options.push({ code: 'en', label: 'English' });

      languageSelect.innerHTML = '';
      options.forEach((option) => {
        const el = document.createElement('option');
        el.value = option.code;
        el.textContent = option.label;
        languageSelect.appendChild(el);
      });

      if (preserveSelection) {
        const stillExists = Array.from(languageSelect.options).some((o) => o.value === previousValue);
        if (stillExists) {
          languageSelect.value = previousValue;
          return;
        }
      }
      languageSelect.selectedIndex = 0;
    }

    countrySelect.addEventListener('change', function () {
      updateHeading();
      updateLanguageOptions(false);
    });

    function findOptionForSimulatedCountry(code) {
      const options = Array.from(countrySelect.options);
      if (code === 'international') {
        return options.find(function (o) {
          return !o.dataset.iso;
        }) || null;
      }
      return options.find(function (o) {
        return o.dataset.iso === code;
      }) || null;
    }

    // Theme Editor-only testing aid: lets a merchant preview how the popup
    // looks for a different detected country without needing a VPN or a
    // fresh browser profile. Gated on Shopify.designMode, which Shopify only
    // sets to true inside the authenticated theme customizer preview — this
    // can never be true for a real storefront visitor, so it can't affect them.
    function applyTestModeSimulation() {
      const simulated = root.dataset.testCountry;
      const isDesignMode = !!(window.Shopify && window.Shopify.designMode);
      if (!simulated || !isDesignMode) return false;
      const targetOption = findOptionForSimulatedCountry(simulated);
      if (!targetOption) return false;
      countrySelect.value = targetOption.value;
      updateHeading();
      updateLanguageOptions(false);
      return true;
    }

    let lastFocused = null;

    function trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusable = modal.querySelectorAll(
        'select, button, a[href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function openPopup() {
      lastFocused = document.activeElement;
      root.classList.add('geo-popup--active');
      modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', trapFocus);
      window.requestAnimationFrame(function () {
        countrySelect.focus();
      });
    }

    function closePopup() {
      root.classList.remove('geo-popup--active');
      modal.hidden = true;
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', trapFocus);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }

    form.addEventListener('submit', function () {
      writePreference(countrySelect.value, languageSelect.value, storageDays);
      // Let the native Shopify localization form submission proceed as normal
      // (real page load to the correct localized/currency URL).
    });

    relabelCountryOptions();

    // Initial language list must reflect the pre-selected (detected) country
    // exactly as rendered server-side, so only rebuild it if it would differ.
    updateLanguageOptions(true);

    const simulating = applyTestModeSimulation();

    if (isLikelyBot()) return;

    const saved = readPreference();
    if (saved && !previewMode && !simulating) return;

    openPopup();
  }

  function init() {
    if (handleResetParamAndReload()) return; // page is about to reload
    document.querySelectorAll('.geo-popup').forEach(initPopup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
